/**
 * useGlSuggestions — ranked GL code suggestions per line item for the review panel.
 *
 * When enabled for a real document, POSTs /documents/{id}/gl-suggestions once (the
 * backend returns the stored set, generating it via one LLM call on first request)
 * and exposes the result as a lineItemId -> suggestions map. Best-effort: a 403
 * (entitlement not enabled), a 404 with no structured body (backend predates the
 * endpoint — disable for the rest of the session) or any other error just leaves
 * the map empty; suggestions never block the review flow.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiException } from "@/services/api";
import { useDocumentsApi } from "@/services/api/documents-api";
import type { GlLineSuggestionsDto, GlSuggestionDto } from "@/types/api/gl-codes";

export function useGlSuggestions(documentId: number | null | undefined, enabled: boolean): Map<number, GlSuggestionDto[]> {
    const documentsApi = useDocumentsApi();
    const [lines, setLines] = useState<GlLineSuggestionsDto[]>([]);
    const isUnsupportedRef = useRef(false);

    useEffect(() => {
        setLines([]);
        // Placeholder/synthetic documents have non-positive ids — nothing to suggest on.
        if (!enabled || !documentId || documentId <= 0 || isUnsupportedRef.current) return;

        let cancelled = false;

        const load = async () => {
            try {
                const res = await documentsApi.generateGlSuggestions(documentId);
                if (!cancelled) setLines(res.lines ?? []);
            } catch (err) {
                if (err instanceof ApiException && err.status === 404 && !err.errorResponse) {
                    isUnsupportedRef.current = true;
                }
                // 403 (feature not enabled) and everything else: silently no suggestions.
                if (!cancelled) setLines([]);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [documentId, enabled, documentsApi]);

    return useMemo(() => {
        const map = new Map<number, GlSuggestionDto[]>();
        for (const line of lines) {
            if (line.suggestions?.length) {
                map.set(
                    line.lineItemId,
                    [...line.suggestions].sort((a, b) => a.rank - b.rank),
                );
            }
        }
        return map;
    }, [lines]);
}
